/**
 * Phonak One-Time Courtesy Replacement Request Form, on a replacement request (2026-10-29).
 * Mirrors backend/src/routes/phonakCourtesyForm.js (mounted under /manifest-intake).
 *
 * Send has three server modes (PHONAK_REPLACEMENT_SEND): off (send refused), review (the email goes only to
 * icotto@emergesc.com) and live (to the Phonak contact, copy to the provider and the sender).
 */
import { apiRequest, apiUpload, apiDownload } from '../../services/api';

const BASE = '/manifest-intake/phonak-forms';

export type PhonakMode = 'off' | 'review' | 'live';
export type PhonakEar = 'RIGHT' | 'LEFT';
/** IN_PROGRESS: a replay of a key whose first send is still running (the send result only). */
export type PhonakSendOutcome = 'SENT' | 'FAILED' | 'UNKNOWN' | 'IN_PROGRESS';

export type PhonakFacts = {
  prepared_at: string | null;
  signed_at: string | null;
  last_review: { at: string; outcome: PhonakSendOutcome } | null;
  last_live: { at: string; outcome: PhonakSendOutcome } | null;
};

export type PhonakRecentItem = {
  request_id: string;
  patient_id: string;
  state: 'OPEN' | 'PLACED';
  side: string;
  reason_words: string;
  requested_at: string;
  placed_at: string | null;
  first_name: string | null;
  last_name: string | null;
  counselear_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  phonak: PhonakFacts;
};

export type PhonakRecent = { items: PhonakRecentItem[]; mode: PhonakMode };

export type PhonakDoc = {
  id: string;
  kind: 'PREPARED' | 'SIGNED';
  at: string;
  by_name?: string | null;
  sha256: string;
  byte_size: number;
  pages?: number;
  continued?: boolean;
  prepared_id?: string;
};

export type PhonakAccount = { account?: string | null; address?: string | null; city?: string | null; state?: string | null; zip?: string | null };

export type PhonakSend = { mode: PhonakMode; outcome: PhonakSendOutcome; at: string; to: string[]; cc: string[]; by_name?: string | null };

export type PhonakStatus = {
  request: {
    id: string; state: string; side: string; sides: PhonakEar[]; reason_code: string; reason_words: string;
    requested_at: string; placed_at: string | null; reference: string; facility_name: string | null;
  };
  available: boolean;
  refusal: { code: string; message: string } | null;
  settings: { configured: boolean; missing: string[]; ship_to: PhonakAccount; bill_to: PhonakAccount; purchase_order_required: boolean };
  prefill: {
    date: string; patient_name: string | null; third_party_patient_number: string | null;
    warranty_expiration: string | null; warranty_source: 'WARRANTY_CHECK' | 'DISPENSE_HINT' | null;
    contact_name: string | null; contact_phone: string | null; statement_name: string | null;
  };
  provider: { source: 'FILED_THE_REQUEST' | 'MARKED_ON_MANIFEST' | 'COVERS_THE_BUILDING' | null; name: string | null; email: string | null; why?: string | null; problem: 'NO_PROVIDER' | 'NO_WORK_EMAIL' | null; message: string | null };
  send: { mode: PhonakMode; enabled: boolean; reason: string | null; live_to: string | null; review_to: string | null; cc: string[] };
  prepared: PhonakDoc[];
  signed: PhonakDoc[];
  sends: PhonakSend[];
  last_sent: { review: PhonakSend | null; live: PhonakSend | null };
  in_flight: boolean;
  umed_help: string;
};

/** The fields of the form; only the ears the request names are sent. */
export type PhonakPrepareBody = {
  model: string;
  serial_right?: string; serial_left?: string;
  receiver_size_right?: string; receiver_size_left?: string;
  cshell_serial_right?: string; cshell_serial_left?: string;
  original_invoice_number: string;
  warranty_expiration: string;
  purchase_order_number: string;
  contact_name: string;
  contact_phone: string;
  circumstances: string;
  statement_name: string;
  idempotency_key: string;
};

export type PhonakSendBody = { document_id: string; idempotency_key: string; confirm_resend?: boolean };
export type PhonakSendResult = { mode: PhonakMode; outcome: PhonakSendOutcome; at: string; to: string[]; cc: string[]; replayed: boolean; message: string };

type Res<T> = { success: boolean; data?: T; message?: string; error?: string; status?: number; code?: string; detail?: any };

export const phonakFormApi = {
  recent: () => apiRequest<PhonakRecent>('GET', BASE) as Promise<Res<PhonakRecent>>,
  status: (requestId: string) => apiRequest<PhonakStatus>('GET', `${BASE}/${encodeURIComponent(requestId)}`) as Promise<Res<PhonakStatus>>,
  prepare: (requestId: string, body: PhonakPrepareBody) =>
    apiRequest<{ document: PhonakDoc; replayed: boolean }>('POST', `${BASE}/${encodeURIComponent(requestId)}/prepare`, body) as Promise<Res<{ document: PhonakDoc; replayed: boolean }>>,
  /**
   * Open a prepared or signed PDF in a new tab. The window is opened first, synchronously (inside the click),
   * so a pop-up blocker allows it. Returns a sentence when it failed, else null.
   */
  openDocument: async (requestId: string, documentId: string): Promise<string | null> => {
    const win = typeof window.open === 'function' ? window.open('', '_blank') : null;
    try {
      const blob = await apiDownload(`${BASE}/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(documentId)}`);
      const href = window.URL.createObjectURL(blob);
      if (win) { win.opener = null; win.location.href = href; } else { window.location.assign(href); }
      window.setTimeout(() => window.URL.revokeObjectURL(href), 60_000);
      return null;
    } catch {
      if (win) win.close();
      return 'The form could not be opened. Try again, or reload the page.';
    }
  },
  uploadSigned: (requestId: string, form: FormData) =>
    apiUpload<{ document: PhonakDoc; replayed: boolean }>(`${BASE}/${encodeURIComponent(requestId)}/signed`, form) as Promise<Res<{ document: PhonakDoc; replayed: boolean }>>,
  send: (requestId: string, body: PhonakSendBody) =>
    apiRequest<PhonakSendResult>('POST', `${BASE}/${encodeURIComponent(requestId)}/send`, body) as Promise<Res<PhonakSendResult>>,
};
